import { Brand } from "@/offering_v2/brand";
import { SpaceOffering } from "../offering/api";
import { isPartnerLocation } from "./api";
import { PartnerCatalog } from "./catalog";
import { ImageVersion, ImageData } from "./image";
import { getLocationGroupValue, Location } from "./location";

export enum TemplateName {
  DayPass = "DayPass",
  MultiPass = "MultiPass",
  EmbedDayPass = "EmbedDayPass",
  EmbedDayOffice = "EmbedDayOffice",
  EmbedMeetingRoom = "EmbedMeetingRoom",
  MultiDayOffice = "MultiDayOffice",
  Membership = "Membership",
}

export enum Template {
  DayPass = "day_pass",
  MultiPass = "multi_pass",
  AddOn = "add_on",
  DailyAccess = "daily_access",
  DayOfficeMultiPass = "day_office_multi_pass",
  DayOffice = "day_office",
  MeetingRoom = "meeting_room",
  SnowflakeDayOfficeListing = "snowflake_day_office_listing",
  SnowflakeDayOffice = "snowflake_day_office",

  // V2 templates
  DailyOffice = "daily_office",
  DailyAccessV2 = "daily_access_v2",
  MeetingRoomV2 = "meeting_room_v2",
  AddOnV2 = "add_on_v2",
  Membership = "membership_v2",
  EventSpace = "event_space_v2",
  RoomGallery = "room_gallery_v2",
}

export enum SortOrder {
  HighestToLowest = "capacity_desc",
  LowestToHighest = "capacity_asc",
}

export enum OfferingItemsGroupBy {
  City = "city",
  Location = "location",
  Neighborhood = "neighborhood",
}

export enum Criterion {
  Capacity = "capacity",
  Location = "location",
  City = "city",
  Datetime = "datetime",
  State = "state",
  Layout = "layout",
  ProductCategory = "product_category",
  ProductType = "product_type",
  LocationGroupedByState = "location_grouped_by_state",
  LocationGroupedByNeighborhood = "location_grouped_by_neighborhood",
}

interface CapacityOption {
  label: string;
  value: { minCapacity?: number; maxCapacity?: number };
}

export interface GalleryFilterConfig {
  city?: boolean;
  location?: boolean;
  capacity?: boolean;
  datetime?: boolean;
  state?: boolean;
  layout?: boolean;
  capacityOptions?: CapacityOption[];
  defaultSortOrder?: SortOrder;
  productCategory?: boolean;
  productType?: boolean;
  locationGroupedByState?: boolean;
  locationGroupedByNeighborhood?: boolean;
}

/** A partner-authored product type (finer-grained than the global
 * ProductCategory taxonomy), as carried by gallery offering items. */
export interface OfferingProductType {
  id: string;
  name: string;
}

export enum ProductCategory {
  MeetingRoom = "meeting_room",
  PrivateOffice = "private_office",
  EventSpace = "event_space",
}

export interface PartnerOffering {
  id: string;
  name: string;
  template: Template;
  offeringTitle?: string;
  description: string;
  partner: Partner;
  addOns: SpaceOffering[];
  offeringLocations: PartnerOfferingLocation[];
  // Unlisted rooms, kept out of offeringLocations so no list can show them. Only a
  // room's own page reads these, through findUnlistedOfferingLocation.
  unlistedOfferingLocations: PartnerOfferingLocation[];
  templateName: TemplateName;
  promoCodeBoxContent?: string;
  generalInfo?: string;
  showDiscountLabel: boolean;
  faqs?: string;
  slug: string;
  usePartnerOfferingImagesAcrossLocations: boolean;
  cancellationPolicy?: string;
  allowSelectableStartDate: boolean;
  singleDaySelect?: boolean;
  quantitySelect?: boolean;
  /** When false, the synthetic single-pass option is hidden so only multi-pass
   * packs are bookable. Defaults to true (single pass shown). */
  showSinglePass?: boolean;
  redirectedSlug?: string;
  hasActiveListingSpaces?: boolean;
  /** Gallery/room listing sort: capacity_asc (default) or capacity_desc. From API offering_items_sort. */
  offeringItemsSort?: SortOrder;
  /** Gallery section grouping: city (default), location or neighborhood. From API offering_items_group_by. */
  offeringItemsGroupBy?: OfferingItemsGroupBy;
  /** When true, the day pass, day office and membership pages pick a
   * neighborhood instead of a city before the location. From API
   * location_grouped_by_neighborhood (the offering's filter criterion). */
  locationGroupedByNeighborhood?: boolean;
  /** When true, gallery "When" filter is daily-only (no hourly/daily switch). From API hide_hourly_gallery_when_filter. */
  hideHourlyGalleryWhenFilter?: boolean;
  /** When true, a meeting room's page suggests the other meeting rooms at its
   * location where the memberships section would be. On for every offering
   * unless ops turn it off in Flex Hub. From API show_related_meeting_room_carousel. */
  showRelatedMeetingRoomCarousel?: boolean;
  /** Which of this offering's locations trade under a brand of their own. Present on the
   * light payload too, so a page can resolve a brand in the same render that yields the
   * partner slug rather than importing the partner stylesheet and reloading afterwards.
   * Locations on the partner's own brand are absent rather than listed as null. */
  locationBrands?: LocationBrand[];
  /** The partner's catalogs that carry a brand of their own. An offering reached from a
   * branded catalog keeps that brand, and `?fromCatalog=` is the only thing that says so
   * on a page whose locations are not narrowed to one. Unbranded catalogs are absent. */
  catalogBrands?: CatalogBrand[];
  /** The brand this offering belongs to as a whole, set by the API only when every
   * location it reaches shares one. This is what brands a page nothing has narrowed —
   * and the only answer available to a customer who arrived without `?fromCatalog=`,
   * from a search result, a shared link or a confirmation email. */
  brand?: Brand;
}

/** One catalog's brand, as the offering payloads carry it. */
export interface CatalogBrand {
  slug: string;
  brand: Brand;
}

/** One location's brand, as the offering payloads carry it. */
export interface LocationBrand {
  id: string;
  /** The location's active spaces. Present so a surface that knows only a space — a
   * checkout route, an order's items — can resolve without a second request. */
  spaceIds?: string[];
  brand: Brand;
}

export type LightPartnerOffering = Pick<
  PartnerOffering,
  | "id"
  | "name"
  | "template"
  | "offeringTitle"
  | "slug"
  | "promoCodeBoxContent"
  | "description"
  | "generalInfo"
  | "showDiscountLabel"
  | "faqs"
  | "partner"
  | "redirectedSlug"
  | "hasActiveListingSpaces"
  | "locationBrands"
  | "catalogBrands"
  | "brand"
>;

export interface Products {
  partner: Partner;
  spaceLocations: PartnerOfferingLocation[];
  addOns: SpaceOffering[];
  templateName: TemplateName;
}

export interface Partner {
  id: string;
  name: string;
  partnerSlug: string;
  homepageUrl: string;
  logoUrl: string;
  internalName: string;
  facebook_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  linkedin_url?: string;
  tiktok_url?: string;
}

export interface PartnerOfferingLocation {
  id: string;
  spaceID: string;
  integrationProductID: string;
  location: Location;
  locationDisplay: string;
  /** The partner-set custom label only, undefined when unset. `locationDisplay`
   * folds in a generated "city - street" fallback, so it can't tell an explicit
   * label apart from the default; this raw value can. */
  customLocationDisplay?: string;
  promoCodeBoxContent?: string;
  featuredPromoDescription?: string;
  generalInfo?: string;
  faqs?: string;
  offeringItemType?: OfferingItemType;
  membership?: MembershipDetail;
  /** The position the partner gave this item in Flex Hub, undefined when they
   * left it unplaced. On an item standing for a whole location it orders
   * locations, not the rooms inside one, and the payload repeats it on every
   * room that item contributes. */
  sortOrder?: number;
  spaceMaxCapacity: number;
  /** How many layouts the room can be arranged in. Absent on older API
   * responses, where it reads as "unknown" rather than "one". */
  spaceLayoutCount?: number;
  /** Per-item resolved single-pass visibility (server already applied the
   * offering-level fallback). Overrides the offering-level showSinglePass. */
  showSinglePass?: boolean;
  productCategory?: ProductCategory;
  productType?: OfferingProductType;
}

export interface FilterLocation {
  id: string;
  locationDisplay: string;
  /** The row label under a neighborhood heading, which already names the area:
   * the partner's own label when set, otherwise the street. */
  streetDisplay: string;
  city: string;
  state: string;
  stateName: string;
  /** The location's neighborhood, or its city when it has none. */
  neighborhood: string;
}

export function toFilterLocationFromOfferingLocation(
  partnerOfferingLocation: PartnerOfferingLocation
): FilterLocation {
  const { location } = partnerOfferingLocation;
  const locationDisplay =
    partnerOfferingLocation.locationDisplay || location.name;
  return {
    id: location.id,
    locationDisplay,
    streetDisplay:
      partnerOfferingLocation.customLocationDisplay ||
      location.streetAddress ||
      locationDisplay,
    city: location.city,
    state: location.state,
    stateName: location.stateName ?? location.city ?? "",
    neighborhood: getLocationGroupValue(location, "neighborhood"),
  };
}

export function toFilterLocationFromLocation(
  location: Location
): FilterLocation {
  const locationDisplay =
    location.customLocationDisplay || location.locationDisplay || location.name;
  return {
    id: location.id,
    locationDisplay,
    streetDisplay:
      location.customLocationDisplay ||
      location.streetAddress ||
      locationDisplay,
    city: location.city,
    state: location.state,
    stateName: location.stateName ?? location.city ?? "",
    neighborhood: getLocationGroupValue(location, "neighborhood"),
  };
}

export function getCatalogFilterLocations(
  locations: FilterLocation[],
  catalog: PartnerCatalog
) {
  return locations?.filter((location) =>
    catalog.partnerCatalogCategories
      .flatMap((category) =>
        category.catalogOfferings?.flatMap((offering) => offering.locationIDs)
      )
      .includes(location.id)
  );
}

export type OfferingItemType = "Location" | "Space" | "Membership";

export type LocatableType = "Location" | "GeoMarket" | "SpacePartner";

export interface GeoMarket {
  id: string;
  name: string;
  description: string;
  locations: Location[];
}

export interface MembershipDetail {
  currency: string;
  description: string;
  id: string;
  locatable?: Location | GeoMarket;
  locatableType: LocatableType;
  monthlyPrice: number;
  name: string;
  partnerId: string;
  stripeProductId: string;
  timeZone: string;
  yearlyPrice: number;
  firstPaymentCycles: number;
  images: ImageVersion[];
  inventory?: number;
  productCategory: string;
  productTypeName: string;
  recurring?: boolean;
  requestToSubscribe: boolean;
  // What the partner wants said once a request to subscribe has been submitted,
  // in the requested locale. Absent on nearly every membership: the API sends it
  // only for a plan that takes requests and whose partner wrote their own
  // wording, so a surface must keep its own default for every other case.
  requestToSubscribeConfirmationText?: string;
  // What the partner wants the checkout button that submits the request to say,
  // in the requested locale. Absent for the same reasons as the wording above, so
  // a surface must keep its own default label for every other case.
  requestToSubscribeCTALabel?: string;
}

export interface OfferingMenu {
  showOfferingMenu: boolean;
  offerings: Offering[];
}

export interface Offering {
  id: string;
  slug: string;
  menuDisplayName: string;
}

export interface PartnerOfferingFilterConfig {
  criterion: string;
  enabled: boolean;
}

function toImageData(data: any): ImageData {
  return {
    url: data.url,
    width: data.width,
    height: data.height,
  };
}

export function toImage(data: any): ImageVersion {
  return {
    original: toImageData(data.original),
    thumb: toImageData(data.thumb),
    small: toImageData(data.small),
    medium: toImageData(data.medium),
    large: toImageData(data.large),
    xlarge: toImageData(data.xlarge),
  };
}

export function toPartnerOfferingFilterConfig(
  data: any
): PartnerOfferingFilterConfig {
  return {
    criterion: data.criterion,
    enabled: data.enabled,
  };
}

// An unlisted room's own page finds no entry for its room in offeringLocations,
// so it reads the room's entry from here to keep its own general info, FAQs and
// promo box.
export function findUnlistedOfferingLocation(
  partnerOffering: PartnerOffering | undefined,
  spaceID: string | undefined
): PartnerOfferingLocation | undefined {
  if (!spaceID) return undefined;

  return partnerOffering?.unlistedOfferingLocations.find(
    (ol) => ol.spaceID === spaceID
  );
}

export function calculateDiscountPercentage(
  multiPass: SpaceOffering,
  singlePass?: SpaceOffering
): number {
  if (!singlePass) return 0;

  if (singlePass.addOnID === multiPass.addOnID) {
    return 0;
  }
  if (!multiPass.numberOfEntries) return 0;

  const originPrice = multiPass.numberOfEntries * singlePass.price;
  const discountedPrice = multiPass.price;
  const discountValue = (originPrice - discountedPrice) / originPrice;

  return Math.round(discountValue * 100);
}

export function toPartnerOfferingImage(data: any): ImageVersion {
  return toImage(data);
}

export function getMembershipInfoFromLocation(
  offeringLocations: PartnerOfferingLocation[],
  locationID?: string
): MembershipDetail | undefined {
  if (!offeringLocations) {
    return undefined;
  }

  let selectedMembership;
  offeringLocations.forEach((loc) => {
    const membership = loc.membership;

    if (isPartnerLocation(membership?.locatable)) {
      if (membership?.locatable.id === locationID) {
        selectedMembership = membership;
      }
    }
  });

  if (!selectedMembership) {
    return (
      offeringLocations.find(
        (loc) => loc.membership?.locatableType === "Location"
      )?.membership || offeringLocations[0].membership
    );
  }

  return selectedMembership;
}

// The membership a customer actually bought, named by its id rather than guessed
// from a location. Its sibling above answers "which membership does this location
// sell", which has to fall back to an arbitrary one when nothing matches; an order
// names its membership exactly, so a miss here means the offering no longer carries
// it and the caller should use its own default rather than another membership's.
export function getMembershipInfoById(
  offeringLocations: PartnerOfferingLocation[],
  membershipID?: string
): MembershipDetail | undefined {
  if (!offeringLocations || !membershipID) {
    return undefined;
  }

  return offeringLocations.find((loc) => loc.membership?.id === membershipID)
    ?.membership;
}

export function getMembershipOfferingLocationFromLocationId(
  offeringLocations: PartnerOfferingLocation[],
  locationID?: string
): PartnerOfferingLocation | undefined {
  if (!offeringLocations) {
    return undefined;
  }

  let selectedOfferingLocation;
  offeringLocations.forEach((loc) => {
    const membership = loc.membership;
    if (isPartnerLocation(membership?.locatable)) {
      if (membership?.locatable.id === locationID) {
        selectedOfferingLocation = loc;
      }
    } else {
      if (
        membership?.locatable?.locations?.some((loc) => loc.id === locationID)
      ) {
        selectedOfferingLocation = loc;
      }
    }
  });

  if (!selectedOfferingLocation) {
    return offeringLocations[0];
  }

  return selectedOfferingLocation;
}

export function hasFoundOfferingFromQueryLocationId(
  offeringLocations?: PartnerOfferingLocation[],
  queryLocationId?: string
) {
  return offeringLocations?.some(
    (loc) =>
      isPartnerLocation(loc.membership?.locatable) &&
      loc.membership?.locatable?.id === queryLocationId
  );
}

export function shouldHideLocationAndCityPicker(
  offeringLocations?: PartnerOfferingLocation[]
) {
  return (
    offeringLocations?.length === 1 &&
    isGeoMarketOrSpacePartner(offeringLocations)
  );
}

export function isGeoMarketOrSpacePartner(
  offeringLocations?: PartnerOfferingLocation[]
) {
  return !!offeringLocations?.some(
    (loc) =>
      loc.membership?.locatableType === "GeoMarket" ||
      loc.membership?.locatableType === "SpacePartner"
  );
}

export const findIsSoldOut = (
  anyLocations: Location[],
  offeringLocations?: PartnerOfferingLocation[]
) => {
  if (!offeringLocations) {
    return false;
  }

  if (!anyLocations.length) {
    return offeringLocations.every(
      (offLoc) => offLoc.membership?.inventory === 0
    );
  }

  return anyLocations.every((anyLoc) => {
    const offeringLocation = offeringLocations.find((loc) => {
      if (isPartnerLocation(loc.membership?.locatable)) {
        return loc.membership?.locatable.id === anyLoc.id;
      }

      return false;
    });

    return offeringLocation && offeringLocation.membership?.inventory === 0;
  });
};

export const getFirstAvailableLocation = (
  locations: Location[],
  offeringLocations: PartnerOfferingLocation[]
) => {
  return locations.find((loc) => {
    const membership = getMembershipInfoFromLocation(offeringLocations, loc.id);

    return membership?.inventory !== 0;
  });
};

export const getMembershipLocations = (
  offeringLocations: PartnerOfferingLocation[]
) => {
  const locations: Location[] = [];

  offeringLocations.forEach((location) => {
    if (
      location.membership?.locatable &&
      isPartnerLocation(location.membership?.locatable)
    ) {
      const foundLocation = location.membership.locatable;
      locations.push({
        ...foundLocation,
        name: location.locationDisplay || foundLocation.name,
      });
    }
  });

  return locations;
};
